import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';

import { TemplatesHttpService } from './templates-http.service';
import { AskTemplatesActions } from './templates.store';

@Injectable({ providedIn: 'root' })
export class AskTemplatesEffects {
  private readonly actions$ = inject(Actions);
  private readonly templatesHttpService = inject(TemplatesHttpService);

  public createTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AskTemplatesActions.createOnePessimistic),
      switchMap(({ payload: { data, correlationId } }) =>
        this.templatesHttpService.createTemplate(data).pipe(
          map(data =>
            AskTemplatesActions.createOnePessimisticSuccess({ payload: { data, correlationId } }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              AskTemplatesActions.createOnePessimisticFailed({ payload: { error, correlationId } }),
            ),
          ),
        ),
      ),
    ),
  );

  public fetchTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AskTemplatesActions.fetchOne),
      switchMap(({ payload: { id } }) =>
        this.templatesHttpService.fetchOne(id).pipe(
          map(data => AskTemplatesActions.fetchOneSuccess({ payload: { data } })),
          catchError((error: HttpErrorResponse) =>
            of(AskTemplatesActions.fetchOneFailed({ payload: { error, id } })),
          ),
        ),
      ),
    ),
  );

  public updateTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AskTemplatesActions.updateOnePessimistic),
      switchMap(({ payload: { data } }) =>
        this.templatesHttpService.updateTemplate(data).pipe(
          map(updatedData =>
            AskTemplatesActions.updateOnePessimisticSuccess({ payload: { data: updatedData } }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(AskTemplatesActions.updateOnePessimisticFailed({ payload: { error, data } })),
          ),
        ),
      ),
    ),
  );

  private deleteTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AskTemplatesActions.removeOnePessimistic),
      switchMap(({ payload: { data } }) =>
        this.templatesHttpService.deleteTemplate(data).pipe(
          map(() => AskTemplatesActions.removeOnePessimisticSuccess({ payload: { id: data } })),
          catchError((error: HttpErrorResponse) =>
            of(AskTemplatesActions.removeOnePessimisticFailed({ payload: { error, data } })),
          ),
        ),
      ),
    ),
  );

  private duplicateTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AskTemplatesActions.duplicateTemplate),
      switchMap(({ payload: { data, correlationId } }) =>
        this.templatesHttpService.createTemplate(data).pipe(
          map(duplicatedData =>
            AskTemplatesActions.duplicateTemplateSuccess({
              payload: { data: duplicatedData, correlationId },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(AskTemplatesActions.duplicateTemplateFailed({ payload: { error, correlationId } })),
          ),
        ),
      ),
    ),
  );
}
